/**
 * The weekly Matchup Report contract.
 *
 * A report reaches the site one of two ways: the weekly fetcher computes one
 * from `LeagueHistory` (`reportBuilder/`, optionally reworded by Claude) and
 * saves it as a `MatchupDraft` for an admin to publish, or an admin uploads a
 * hand-authored one. Either way it's validated by `matchupReportProblems`
 * below (via `packages/api`'s `matchupReportEditor.ts`) and rendered by
 * `packages/web`'s Matchups page. See `docs/matchup-report.md` for the
 * authoring rules.
 *
 * Numbers the UI draws from (records, scores, recent results) are structured
 * fields; the skill's narrative color lives in free-text `facts`, so a new
 * kind of fact never needs a schema change.
 */

export const MATCHUP_REPORT_SCHEMA_VERSION = 1;

/** Sentinel used in `Rivalry.recentResults` for a tied meeting. */
export const TIE_RESULT = "tie";

/** Two `ReportManager.id`s. Every `[a, b]` tuple on the same object is aligned to this order. */
export type ManagerPair = [string, string];

/** An order-independent key for a pair of manager ids, so `[a, b]` and `[b, a]` match. */
export function managerPairKey(ids: readonly string[]): string {
  return [...ids].sort().join("|");
}

/** A pair of numbers aligned to the owning object's `managerIds`. */
export type PairValues = [number, number];

export interface ReportManager {
  /** Stable slug the skill assigns (e.g. "blake") — must not change week to week. */
  id: string;
  /** The real person's name. */
  name: string;
  /** This season's team name. */
  teamName: string;
  logoUrl?: string | null;
  /** Current-season record as of this report. */
  record: { wins: number; losses: number; ties: number };
  /** Current-season standings rank, or null before any games are final. */
  rank: number | null;
  /** Current-season points for. */
  pointsFor: number;
  /** Seasons this manager won the championship. */
  championships?: number[];
}

/** One narrative bullet. `tone` only affects styling. */
export interface Fact {
  emoji?: string;
  text: string;
  tone?: "hot" | "cold" | "neutral";
  /**
   * Which computed angles (e.g. "streak") the bullet states, so next week's
   * generated report can rotate them out (see `rotationOf`). Not displayed;
   * absent on hand-written facts.
   */
  kinds?: string[];
}

export type KeyGameKind =
  | "lastMeeting"
  | "biggestBlowout"
  | "closestGame"
  | "highestScoring"
  | "lowestScore"
  | "playoff"
  | "custom";

/** Display labels for the standard kinds; a `"custom"` game supplies its own `title`. */
export const KEY_GAME_LABELS: Record<Exclude<KeyGameKind, "custom">, string> = {
  lastMeeting: "Last meeting",
  biggestBlowout: "Biggest blowout",
  closestGame: "Closest game",
  highestScoring: "Highest scoring",
  lowestScore: "Rock bottom",
  playoff: "Playoff clash",
};

/** A notable past meeting between the two managers of a `Rivalry`. */
export interface KeyGame {
  kind: KeyGameKind;
  /** Overrides the standard label; required when `kind` is `"custom"`. */
  title?: string;
  season: number;
  week: number;
  isPlayoff: boolean;
  scores: PairValues;
  projected?: PairValues;
  note?: string;
}

/** All-time head-to-head history between two managers. */
export interface Rivalry {
  managerIds: ManagerPair;
  /** e.g. "The Nemesis", "The Pendulum". */
  nickname?: string;
  gamesPlayed: number;
  wins: PairValues;
  ties: number;
  pointsPerGame: PairValues;
  playoffWins?: PairValues;
  /** Winner's manager id (or `TIE_RESULT`) per meeting, oldest → newest, at most the last 10. */
  recentResults: string[];
  keyGames: KeyGame[];
  /** Lasting, not-week-specific facts about this rivalry. */
  facts: Fact[];
}

/** One game on this week's schedule. Its history lives in the matching `Rivalry`. */
export interface WeeklyMatchup {
  managerIds: ManagerPair;
  /** e.g. "Defending champ vs the winless team". */
  headline?: string;
  /** This-week-only facts (current form, week-N records). */
  facts: Fact[];
}

/** A league-wide one-liner award (e.g. "🔥 Hottest start"). */
export interface QuickHit {
  /** Which award this is (e.g. "hotBench"), so next week's report can rotate it out; absent on hand-written hits. */
  id?: string;
  emoji: string;
  label: string;
  managerIds: string[];
  detail: string;
}

/** The `data/matchups/<season>/week-<n>.json` file. */
export interface MatchupReport {
  schemaVersion: typeof MATCHUP_REPORT_SCHEMA_VERSION;
  season: number;
  week: number;
  generatedAt: string;
  /** Human-readable scope of the history, e.g. "2016 onward, includes playoff games". */
  coverage: string;
  managers: ReportManager[];
  /** This week's games, in display order. */
  matchups: WeeklyMatchup[];
  /** Every pair of `managers` that has ever met — not only this week's. */
  rivalries: Rivalry[];
  quickHits: QuickHit[];
  /**
   * Bookkeeping for the generated report's week-to-week variety: which
   * angles it picked, so next week's build favors others. Not displayed.
   * Absent on hand-written reports, which simply apply no rotation.
   */
  rotation?: ReportRotation;
}

/** What a generated report picked; see `MatchupReport.rotation`. */
export interface ReportRotation {
  /** Fact kind (e.g. "weekRecord") -> how many of this week's games used it. */
  factKinds: Record<string, number>;
  /**
   * The same tally summed over this season's earlier generated reports, so
   * a build can favor kinds the season hasn't seen yet (see `seasonFactKinds`).
   * Absent on reports from before it was tracked.
   */
  earlierFactKinds?: Record<string, number>;
  /** `QuickHit.id`s shown. */
  quickHitIds: string[];
}

export interface MatchupReportIndexEntry {
  season: number;
  week: number;
  generatedAt: string;
}

/** Token counts for one Claude call, as the API reported them — for tracking what the weekly draft costs. */
export interface ClaudeUsage {
  /** The model that actually answered (a server-side fallback can differ from the one requested). */
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
}

/**
 * The `data/private/matchup-draft.json` file — the weekly fetcher's generated
 * report awaiting an admin's review. Not publicly readable (the bucket policy
 * denies CloudFront `data/private/*`); only the api Lambda serves it, to
 * signed-in users, and publishing copies `report` through the normal upload path.
 */
export interface MatchupDraft {
  report: MatchupReport;
  createdAt: string;
  /** "claude" when the prose pass succeeded, "computed" when it was skipped or failed. */
  writtenBy: "claude" | "computed";
  /** Why the prose pass was skipped/failed, or anything else the reviewer should know. */
  notes: string[];
  /** Set whenever Claude replied, including a reply that was then discarded; absent when no call was billed. */
  usage?: ClaudeUsage;
}

/** The `data/matchups/index.json` file — which reports exist, newest first. */
export interface MatchupReportIndex {
  updatedAt: string;
  reports: MatchupReportIndexEntry[];
}

const KEY_GAME_KINDS: readonly KeyGameKind[] = [
  "lastMeeting",
  "biggestBlowout",
  "closestGame",
  "highestScoring",
  "lowestScore",
  "playoff",
  "custom",
];
const FACT_TONES: readonly (Fact["tone"] | undefined)[] = [undefined, "hot", "cold", "neutral"];

/**
 * Every internal-consistency problem in a report (empty when it's valid):
 * known manager ids, aligned pairs, records that add up, known enum values.
 * Shared by the api's upload validation and the fetcher's generated drafts.
 */
export function matchupReportProblems(report: MatchupReport): string[] {
  const problems: string[] = [];

  if (report.schemaVersion !== MATCHUP_REPORT_SCHEMA_VERSION) {
    problems.push(
      `schemaVersion must be ${MATCHUP_REPORT_SCHEMA_VERSION}, got ${String(report.schemaVersion)}`,
    );
  }
  const ids = new Set<string>();
  for (const manager of report.managers) {
    if (!manager.id.trim()) problems.push(`manager "${manager.name}" has an empty id`);
    if (ids.has(manager.id)) problems.push(`manager id "${manager.id}" is used more than once`);
    ids.add(manager.id);
  }

  const checkPair = (pair: [string, string], where: string) => {
    for (const id of pair) {
      if (!ids.has(id)) problems.push(`${where} references unknown manager id "${id}"`);
    }
    if (pair[0] === pair[1]) problems.push(`${where} pairs "${pair[0]}" with itself`);
  };
  const checkFacts = (facts: Fact[], where: string) => {
    facts.forEach((fact, i) => {
      if (!FACT_TONES.includes(fact.tone)) {
        problems.push(`${where} fact ${i + 1} has an invalid tone, got ${String(fact.tone)}`);
      }
    });
  };

  report.matchups.forEach((matchup, i) => {
    checkPair(matchup.managerIds, `matchup ${i + 1}`);
    checkFacts(matchup.facts, `matchup ${i + 1}`);
  });

  const seenPairs = new Set<string>();
  report.rivalries.forEach((rivalry, i) => {
    const where = `rivalry ${i + 1} (${rivalry.managerIds.join(" vs ")})`;
    checkPair(rivalry.managerIds, where);
    checkFacts(rivalry.facts, where);

    const pairKey = managerPairKey(rivalry.managerIds);
    if (seenPairs.has(pairKey)) problems.push(`${where} appears more than once`);
    seenPairs.add(pairKey);

    const decided = rivalry.wins[0] + rivalry.wins[1] + rivalry.ties;
    if (decided !== rivalry.gamesPlayed) {
      problems.push(
        `${where}: wins + ties (${decided}) must equal gamesPlayed (${rivalry.gamesPlayed})`,
      );
    }
    rivalry.recentResults.forEach((result) => {
      if (result !== TIE_RESULT && !rivalry.managerIds.includes(result)) {
        problems.push(`${where}: recent result "${result}" must be one of the pair or "${TIE_RESULT}"`);
      }
    });
    rivalry.keyGames.forEach((game, j) => {
      if (!KEY_GAME_KINDS.includes(game.kind)) {
        problems.push(`${where} key game ${j + 1} has an unknown kind "${game.kind}"`);
      }
      if (game.kind === "custom" && !game.title?.trim()) {
        problems.push(`${where} key game ${j + 1} is "custom" but has no title`);
      }
    });
  });

  report.quickHits.forEach((hit, i) => {
    hit.managerIds.forEach((id) => {
      if (!ids.has(id)) problems.push(`quick hit ${i + 1} references unknown manager id "${id}"`);
    });
  });

  return problems;
}

/**
 * What a report's text actually shows, as `MatchupReport.rotation`: for each
 * fact kind, how many of this week's games (their facts plus their rivalry's)
 * state it, and which quick hits ran. Computed from the final text, so a
 * fact the Claude pass swapped out isn't counted and one it swapped in is.
 * `earlierFactKinds` defaults to the report's own, so recomputing a
 * rewritten report's rotation keeps the season tally its build started with.
 */
export function rotationOf(
  report: MatchupReport,
  earlierFactKinds: Record<string, number> | undefined = report.rotation?.earlierFactKinds,
): ReportRotation {
  const factKinds: Record<string, number> = {};
  for (const matchup of report.matchups) {
    const facts = [...matchup.facts, ...(findRivalry(report, ...matchup.managerIds)?.facts ?? [])];
    for (const kind of new Set(facts.flatMap((f) => f.kinds ?? []))) factKinds[kind] = (factKinds[kind] ?? 0) + 1;
  }
  return {
    factKinds,
    ...(earlierFactKinds && { earlierFactKinds }),
    quickHitIds: report.quickHits.flatMap((h) => (h.id ? [h.id] : [])),
  };
}

/**
 * Games per fact kind across the season's generated reports through the one
 * `rotation` belongs to. A report from before the season tally was tracked
 * counts only its own week.
 */
export function seasonFactKinds(rotation: ReportRotation | undefined): Record<string, number> {
  const total: Record<string, number> = { ...rotation?.earlierFactKinds };
  for (const [kind, games] of Object.entries(rotation?.factKinds ?? {})) total[kind] = (total[kind] ?? 0) + games;
  return total;
}

/** Display label for a key game: its own `title`, else the standard one for its kind. */
export function keyGameLabel(game: KeyGame): string {
  if (game.title) return game.title;
  return game.kind === "custom" ? "Notable game" : KEY_GAME_LABELS[game.kind];
}

function swap(values: PairValues): PairValues {
  return [values[1], values[0]];
}

/**
 * The rivalry between `idA` and `idB`, regardless of which order the skill
 * wrote the pair in, with every aligned tuple flipped so index 0 is `idA`.
 * Null when the two have never met (no rivalry entry).
 */
export function findRivalry(report: MatchupReport, idA: string, idB: string): Rivalry | null {
  const rivalry = report.rivalries.find(
    (r) =>
      (r.managerIds[0] === idA && r.managerIds[1] === idB) ||
      (r.managerIds[0] === idB && r.managerIds[1] === idA),
  );
  if (!rivalry) return null;
  if (rivalry.managerIds[0] === idA) return rivalry;

  return {
    ...rivalry,
    managerIds: [idA, idB],
    wins: swap(rivalry.wins),
    pointsPerGame: swap(rivalry.pointsPerGame),
    ...(rivalry.playoffWins && { playoffWins: swap(rivalry.playoffWins) }),
    keyGames: rivalry.keyGames.map((game) => ({
      ...game,
      scores: swap(game.scores),
      ...(game.projected && { projected: swap(game.projected) }),
    })),
  };
}
